/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import get from 'lodash/get';
import { Action, ActionCreators } from './actions';
import isNil from 'lodash/isNil';
import { SteerableBeamsRing } from '../../shared/entities/steerableBeam';
export interface State {
  isRequestingSteerableBeams: boolean;
  isInitialSteerableBeamsRequestFinished: boolean;
  requestSteerableBeamsError?: string;
  sourceTime: number;
  radiusNauticalMiles: number;
  distanceToBeamEdgeNmMinorThreshold: number;
  distanceToBeamEdgeNmMajorThreshold: number;
  beams: SteerableBeamsRing[];
  primaryDatapathUuids: string[];
  secondaryDatapathUuids: string[];
}

export const initialSteerableBeamsState: State = {
  isRequestingSteerableBeams: false,
  isInitialSteerableBeamsRequestFinished: false,
  requestSteerableBeamsError: undefined,
  sourceTime: 0,
  radiusNauticalMiles: 300,
  distanceToBeamEdgeNmMinorThreshold: 200,
  distanceToBeamEdgeNmMajorThreshold: 50,
  beams: [],
  primaryDatapathUuids: [],
  secondaryDatapathUuids: [],
};

export default function reducer(state: State = initialSteerableBeamsState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    case ActionCreators.RequestSteerableBeams.type:
      partialState = {
        isRequestingSteerableBeams: true,
        isInitialSteerableBeamsRequestFinished: false,
        requestSteerableBeamsError: undefined,
      };
      break;

    case ActionCreators.ReceivedSteerableBeamsSuccess.type: {
      const steerableBeamsRings = get(action, 'payload.response.beams', []);
      const primaryAircraftIds = steerableBeamsRings.map((ring: SteerableBeamsRing) => ring.primaryDatapathUuid);
      const secondaryAircraftIds = steerableBeamsRings.reduce(
        (accumSecondary: string[], currentVal: SteerableBeamsRing) => {
          if (!isNil(currentVal) && !isNil(currentVal.secondaryDatapathUuids)) {
            accumSecondary = accumSecondary.concat(currentVal.secondaryDatapathUuids);
          }
          return accumSecondary;
        },
        [],
      );
      partialState = {
        sourceTime: get(action, 'payload.response.sourceTime', 0),
        radiusNauticalMiles: get(action, 'payload.response.radiusNauticalMiles', 300),
        distanceToBeamEdgeNmMinorThreshold: get(action, 'payload.response.distanceToBeamEdgeNmMinorThreshold', 200),
        distanceToBeamEdgeNmMajorThreshold: get(action, 'payload.response.distanceToBeamEdgeNmMajorThreshold', 50),
        beams: steerableBeamsRings,
        primaryDatapathUuids: primaryAircraftIds,
        secondaryDatapathUuids: secondaryAircraftIds,
        isRequestingSteerableBeams: false,
        isInitialSteerableBeamsRequestFinished: true,
        requestSteerableBeamsError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedSteerableBeamsError.type:
      partialState = {
        isRequestingSteerableBeams: false,
        isInitialSteerableBeamsRequestFinished: true,
        requestSteerableBeamsError: action.payload.message,
      };
      break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}
